import {
  GET_FLEET_INFORMATION,
  HANDLE_FLEET_VEHICLE_SELECT,
  CLEAR_ALL_SELECTED_TRUCKS,
  UPDATE_FLEET,
  SET_SOURCE_VEHICLE,
} from 'actions/fleet'
import { Map } from 'immutable'
import { createReducer } from 'reducers/utils'
import { map, compose } from 'ramda'
import { set, setIn, getIn, getInOr } from 'utils/immutable'

export const initialState = new Map({
  isWaiting: false,
  fleets: new Map(),
})

const handlers = {
  [GET_FLEET_INFORMATION]: fleetInfoRecieved,
  [UPDATE_FLEET]: setCommitInProgress,
  [HANDLE_FLEET_VEHICLE_SELECT]: updateVehicleSelected,
  [CLEAR_ALL_SELECTED_TRUCKS]: deselectAllFleetTrucks,
  [SET_SOURCE_VEHICLE]: setSourceVehicle,
}

export default createReducer(initialState, handlers)

const vehiclesPath = (fleetUri) => ['fleets', fleetUri, 'vehicles']

function setSourceVehicle(state, { serialNumber, fleetUri }) {
  return setIn(['fleets', fleetUri, 'sourceSerialNumber'], serialNumber)(state)
}

function fleetInfoRecieved(state, { vehicles, fleetName }) {
  // The API response identifies the lead vehicle via isLeadSerial: true on the
  // vehicle object, not via a per-vehicle leadSerialNumber field. Fall back to
  // vehicles[0] in case the flag is absent so the reducer never crashes.
  const leadVehicle = vehicles.find((v) => v.isLeadSerial) ?? vehicles[0]
  const leadSerialNumber = leadVehicle?.serialNumber

  // An empty or malformed response yields no lead serial. Persisting under
  // '/vehicles/undefined' would cause the selector to miss the entry and leave
  // the fleet page in a permanent loading state. Return the current state
  // unchanged so any previously stored fleet data is preserved.
  if (!leadSerialNumber) return state

  const fleetUri = `/vehicles/${leadSerialNumber}`
  // if sourceSerialNumber exist, use it, else set to new leadSerialNumber
  const sourceSerialNumber = getInOr(
    leadSerialNumber,
    ['fleets', fleetUri, 'sourceSerialNumber'],
    state
  )

  return setIn(['fleets', fleetUri], {
    // fleetName comes from the top-level `fleet` string in the API response,
    // forwarded by the fleetSizeReceived saga. Fall back to a per-vehicle
    // customerName field (legacy shape) or empty string so the UI never shows
    // undefined.
    fleetName: fleetName ?? leadVehicle?.customerName ?? '',
    vehicles: vehicles.map((vehicle) => ({ ...vehicle, selected: false })),
    commitInProgress: false,
    sourceSerialNumber,
  })(state)
}

function setCommitInProgress(state, { fleetUri }) {
  const path = ['fleets', fleetUri, 'commitInProgress']
  const status = getIn(path)(state)
  return setIn(path, !status)(state)
}

function updateVehicleSelected(state, { payload }) {
  const { serialNumber, fleetUri } = payload

  const path = vehiclesPath(fleetUri)
  const vehicles = state.getIn(path)
  const updatedVehiclesList = vehicles.map((veh) => {
    const isTruck = veh.get('serialNumber') === serialNumber
    if (!isTruck) return veh
    const selected = veh.get('selected')
    return veh.set('selected', !selected)
  })

  return setIn(path, updatedVehiclesList)(state)
}

function deselectAllFleetTrucks(state, { payload }) {
  const { fleetUri } = payload
  const path = vehiclesPath(fleetUri)

  const deselectedVehiclesList = compose(
    map(set('selected', false)),
    getIn(path)
  )(state)

  return setIn(path, deselectedVehiclesList)(state)
}
